import { forwardRef, useState, useRef, useEffect, useImperativeHandle } from 'react'
import classNames from 'classnames'
import { useTabsContext } from './context'
import type { CommonProps } from '../@types/common'
import type { TabsValue } from './context'
import type { ReactNode } from 'react'

export interface TabNavProps extends CommonProps {
    children?: ReactNode
    disabled?: boolean
    value?: TabsValue
}

const TabNav = forwardRef<HTMLDivElement, TabNavProps>((props, ref) => {
    const { value, disabled, className, children, ...rest } = props

    const { value: currentValue, onValueChange, variant } = useTabsContext()
    const [ripple, setRipple] = useState<{ x: number, y: number, size: number } | null>(null)
    const tabRef = useRef<HTMLDivElement>(null)
    
    // 将内部ref暴露给外部ref
    useImperativeHandle(ref, () => tabRef.current as HTMLDivElement)

    const active = currentValue === value

    const onTabNavClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (disabled) {
            return
        }

        // 创建波纹效果
        if (tabRef.current) {
            const rect = tabRef.current.getBoundingClientRect()
            const size = Math.max(rect.width, rect.height)
            const x = e.clientX - rect.left
            const y = e.clientY - rect.top
            setRipple({ x, y, size })
        }

        if (value !== undefined) {
            onValueChange?.(value)
        }
    }

    // 波纹效果结束后清除
    useEffect(() => {
        if (ripple) {
            const timer = setTimeout(() => {
                setRipple(null)
            }, 600)
            return () => clearTimeout(timer)
        }
    }, [ripple])

    const tabNavClass = classNames(
        'tab-nav',
        `tab-nav-${variant}`,
        active && 'tab-nav-active',
        disabled && 'tab-nav-disabled',
        'relative overflow-hidden', // 为波纹效果添加overflow-hidden
        className
    )

    return (
        <div
            ref={tabRef}
            className={tabNavClass}
            role="tab"
            aria-selected={active}
            tabIndex={0}
            onClick={onTabNavClick}
            onKeyDown={(e) => {
                if (e.key === 'Enter') {
                    onTabNavClick(e as unknown as React.MouseEvent<HTMLDivElement>)
                }
            }}
            {...rest}
        >
            {children}
            {ripple && (
                <span
                    className="absolute bg-primary-200 dark:bg-primary-700 opacity-40 rounded-full pointer-events-none animate-ripple"
                    style={{
                        left: ripple.x - ripple.size / 2,
                        top: ripple.y - ripple.size / 2,
                        width: ripple.size,
                        height: ripple.size,
                    }}
                />
            )}
        </div>
    )
})

TabNav.displayName = 'TabNav'

export default TabNav 